import React, { useState } from 'react';
import { usePageMeta } from '../lib/usePageMeta';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import PrincipleBlock from '../components/PrincipleBlock';
import CTABar from '../components/CTABar';
import InterestFormModal from '../components/InterestFormModal';

const PRINCIPLES_COMPACT = [
  { number: '01', title: 'Deterministic, Not Probabilistic', body: 'Every calculation traces to source. Auditable logic, versioned rules.' },
  { number: '02', title: 'Precision Over Speed', body: 'Built for defensibility. If it cannot survive a CFO\'s scrutiny, it is not ready.' },
  { number: '03', title: 'Designed to Hand Off', body: 'Built so your team owns it after we leave. No vendor lock-in.' },
  { number: '04', title: 'Compliance by Architecture', body: 'SOC 2, HIPAA, GDPR built into the foundation, not bolted on.' },
];

const HowWeWork: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  usePageMeta(
    'How We Work | Parsant',
    'Three engagement modes, one sequence. Plan, Embed, Hand-off. What each mode looks like week by week, and how every engagement ends with your team owning the system.'
  );

  return (
    <main className="bg-void-black min-h-screen">
      <Nav onContactClick={() => setIsFormOpen(true)} />

      <section className="pt-32 pb-16">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow eyebrow-accent mb-4">How we work</div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-signal-white mb-6 tracking-tight leading-[1.08] max-w-4xl">
            Three modes. One sequence.
          </h1>
          <p className="text-xl text-steel measure">
            Most engagements start with Plan and extend through Embed. Hand-off is the exit, not
            an option.
          </p>
        </div>
      </section>

      <section className="section border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6 space-y-6">
          <div className="eyebrow">01 · Plan</div>
          <h2 className="text-3xl md:text-4xl font-bold text-signal-white max-w-4xl">What four weeks actually looks like.</h2>
          {/* Commercial terms in the Services price treatment: mono tabular
              number at display scale, mono uppercase micro labels beside it.
              They used to ride inside the 12px eyebrow, which wrapped to
              three lines at 390 and left trailing middle dots. */}
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
            <span
              className="font-mono text-2xl md:text-3xl text-signal-white whitespace-nowrap"
              style={{ fontFeatureSettings: '"tnum"' }}
            >
              $15K-$30K
            </span>
            <span className="font-mono text-xs text-steel uppercase tracking-[0.15em]">2 to 4 weeks</span>
            <span className="font-mono text-xs text-steel uppercase tracking-[0.15em]">Fixed-fee</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-grid-line mt-8">
            {[
              { wk: 'Week 1', label: 'Discovery', body: 'Interviews with operators across 1 to 3 teams. We watch the work happen. No survey.' },
              { wk: 'Week 2', label: 'Architecture sketch', body: 'Candidate workflows scored. For each, defensible value-at-stake, integration risk, and failure modes.' },
              { wk: 'Week 3', label: 'Roadmap draft', body: 'Prioritized two-quarter roadmap. Three to five workstreams scoped, two to three explicitly de-prioritized.' },
              { wk: 'Week 4', label: 'Board-ready deliverable', body: 'Final roadmap, scoped costs, the candid "don\'t build this yet" calls, and a 30-minute walkthrough.' },
            ].map((s, i) => (
              <div key={i} className="bg-void-black p-6">
                <div className="font-mono text-xs text-command-amber uppercase tracking-[0.15em] mb-2">{s.wk}</div>
                <h3 className="text-lg font-semibold text-signal-white mb-3">{s.label}</h3>
                <p className="text-steel text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-tight border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6 space-y-6">
          <div className="eyebrow">02 · Embed</div>
          <h2 className="text-3xl md:text-4xl font-bold text-signal-white max-w-4xl">Embedded with the team, not parked alongside.</h2>
          {/* Commercial terms in the Services price treatment: mono tabular
              number at display scale, mono uppercase micro labels beside it.
              They used to ride inside the 12px eyebrow, which wrapped to
              three lines at 390 and left trailing middle dots. */}
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
            <span
              className="font-mono text-2xl md:text-3xl text-signal-white whitespace-nowrap"
              style={{ fontFeatureSettings: '"tnum"' }}
            >
              $15K-$25K / mo
            </span>
            <span className="font-mono text-xs text-steel uppercase tracking-[0.15em]">3 to 6 months</span>
            <span className="font-mono text-xs text-steel uppercase tracking-[0.15em]">Monthly subscription</span>
          </div>
          <div className="text-steel text-lg leading-relaxed space-y-4 measure">
            <p>A Parsant senior operator embeds part-time with your executive team. Cadence is weekly cross-functional working sessions, biweekly sponsor sync, monthly executive review. We build inside your existing infrastructure and recommend specific architecture, tooling, or hardware additions on a case-by-case basis, with a written rationale before any commitment.</p>
            <p>An on-demand contract engineering bench scales delivery capacity per project. Scope is reviewed at every monthly milestone. If the engagement no longer earns its keep, you can end it at month-end with no penalty.</p>
            <p>One senior lead owns the engagement end to end, with contract engineering added per project. Your work is never passed down to a junior account team.</p>
          </div>
        </div>
      </section>

      <section className="section-tight border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6 space-y-6">
          <div className="eyebrow">03 · Hand-off</div>
          <h2 className="text-3xl md:text-4xl font-bold text-signal-white max-w-4xl">The exit is a deliverable.</h2>
          {/* Commercial terms in the Services price treatment: mono tabular
              number at display scale, mono uppercase micro labels beside it.
              They used to ride inside the 12px eyebrow, which wrapped to
              three lines at 390 and left trailing middle dots. */}
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
            <span
              className="font-mono text-2xl md:text-3xl text-signal-white whitespace-nowrap"
              style={{ fontFeatureSettings: '"tnum"' }}
            >
              $15K-$30K
            </span>
            <span className="font-mono text-xs text-steel uppercase tracking-[0.15em]">1 to 2 months</span>
            <span className="font-mono text-xs text-steel uppercase tracking-[0.15em]">Fixed-fee</span>
          </div>
          <div className="text-steel text-lg leading-relaxed space-y-4 measure">
            <p>You receive interactive playbooks, layered systems diagrams, error-handling runbooks, and a model-swap policy. We also work with you to identify and onboard the internal owner who will run the system after we leave.</p>
            <p>Exit criteria signed off before close. Day-one operability is the bar. The morning after Parsant leaves, your team should be able to extend the system without us in the room. If that bar is not met, the engagement is not over.</p>
            <p>We do not take an interim executive role and we do not manage the sales team.</p>
          </div>
        </div>
      </section>

      <section className="section-loose border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow mb-4">How we build</div>
          <h2 className="text-3xl font-bold text-signal-white mb-12">Four principles we build by.</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-grid-line">
            {PRINCIPLES_COMPACT.map(p => (
              <div key={p.number} className="bg-void-black">
                <PrincipleBlock variant="compact" {...p} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-cta border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <CTABar onContactClick={() => setIsFormOpen(true)} />
        </div>
      </section>

      <Footer />
      <InterestFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </main>
  );
};

export default HowWeWork;
